import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const beginnerGuide = {
  slug: 'usfans-spreadsheet-beginner-guide',
  route: '/usfans-spreadsheet-beginner-guide',
  titleKey: 'art.title',
  image:
    'https://readdy.ai/api/search-image?query=Clean%20minimalist%20flat%20lay%20of%20a%20notebook%20pen%20and%20smartphone%20with%20a%20checklist%20on%20a%20soft%20cream%20desk%20background%2C%20fresh%20airy%20aesthetic%2C%20soft%20natural%20lighting%2C%20high%20detail%20modern%20European%20editorial%20style&width=800&height=500&seq=guide-beginner-01&orientation=landscape',
};

export default function GuidesFaq() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    { q: t('home.faqQ1'), a: t('home.faqA1') },
    { q: t('home.faqQ2'), a: t('home.faqA2') },
    { q: t('home.faqQ3'), a: t('home.faqA3') },
    { q: t('home.faqQ4'), a: t('home.faqA4') },
    { q: t('home.faqQ5'), a: t('home.faqA5') },
  ];

  const allArticles = [
    beginnerGuide,
    ...articles.map((a) => ({
      slug: a.slug,
      route: `/guide/${a.slug}`,
      titleKey: `articles.${a.slug}.title`,
      image: a.image,
    })),
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20 space-y-16">
      {/* Latest Guides */}
      <section id="guides">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center">{t('home.guidesTitle')}</h2>
        <p className="mt-4 text-sm md:text-base text-stone-600 text-center">{t('home.guidesSubtitle')}</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
          {allArticles.map((a) => (
            <Link
              key={a.slug}
              to={a.route}
              className="group rounded-lg border border-stone-200 bg-white overflow-hidden hover:border-emerald-300 transition-colors cursor-pointer flex flex-col"
            >
              <div className="w-full h-36 md:h-44 overflow-hidden">
                <img
                  src={a.image}
                  alt={t(a.titleKey)}
                  title={t(a.titleKey)}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-4 flex flex-col flex-1">
                <span className="inline-block self-start rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800">
                  {t('home.navGuides')}
                </span>
                <h3 className="mt-2 font-heading font-semibold text-stone-900 text-sm leading-snug line-clamp-2 flex-1">
                  {t(a.titleKey)}
                </h3>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[11px] text-stone-500">{t('home.guidesUpdated')} 2026</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    {t('home.guidesRead')}
                    <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto w-full">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center">{t('home.faqTitle')}</h2>
        <div className="mt-8 space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-lg border border-stone-200 bg-white overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
              >
                <span className="font-medium text-stone-900 text-sm md:text-base">{f.q}</span>
                <i
                  className={`ri-add-line text-xl text-emerald-600 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}
                />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 text-sm text-stone-600 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="rounded-lg bg-emerald-600 px-6 py-12 md:py-16 text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-white">{t('home.finalTitle')}</h2>
        <p className="mt-4 text-sm md:text-base text-emerald-50 max-w-2xl mx-auto leading-relaxed">
          {t('home.finalSubtitle')}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="rounded-md bg-white px-6 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 text-center whitespace-nowrap cursor-pointer"
          >
            {t('home.finalCta')}
          </a>
          <Link
            to="/usfans-spreadsheet-beginner-guide"
            className="rounded-md border border-white/60 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 text-center whitespace-nowrap cursor-pointer"
          >
            {t('home.ctaGuide')}
          </Link>
        </div>
      </section>
    </div>
  );
}